import React, { useState } from 'react'
import { View, Text, StyleSheet, ScrollView, Image, TouchableOpacity } from 'react-native'
import { useNavigation, useRoute, useTheme } from '@react-navigation/native'
import { Header, Notes, SearchBar } from '@components'
import { Theme } from '@theme'
import { useNotes } from './hooks'
import { Contact, useGetContactById, useScreen, usePaginateContacts } from '@hooks'
import { EditNote } from './EditNote'
import { getCommonStyles, Scope } from '@utils'
import ContactsFilter from '../components/ContactsFilter'
import { ContactsList } from '../../contacts/listingContacts/components'
import { SafeAreaView } from 'react-native-safe-area-context'
import { ActivityIndicator } from 'react-native'
import { defaultImage } from '@assets'
import { useLanguage } from '@context'

const SelectedContactDisplay: React.FC<{
  contactId: string
  onPress: () => void
}> = ({ contactId, onPress }) => {
  const theme = useTheme()
  const styles = getStyles(theme)
  const commonStyles = getCommonStyles(theme)
  const { data: contact, isLoading } = useGetContactById(contactId)

  if (isLoading) {
    return (
      <View style={[commonStyles.row, styles.contactContainer]}>
        <ActivityIndicator size="small" />
      </View>
    )
  }

  if (!contact) {
    return null
  }

  return (
    <TouchableOpacity style={[commonStyles.row, styles.contactContainer]} onPress={onPress}>
      <Image
        source={contact.avatarUrl ? { uri: contact.avatarUrl } : defaultImage}
        style={styles.contactAvatar}
      />
      <Text style={styles.contactName}>{contact.fullName}</Text>
    </TouchableOpacity>
  )
}

export const NotesScreen: React.FC = () => {
  const navigation = useNavigation<any>()
  const route = useRoute<any>()
  const profileId = route.params?.profileId
  const theme = useTheme()
  const { t } = useLanguage()
  const commonStyles = getCommonStyles(theme)
  const styles = getStyles(theme)
  const { isWeb, isMobile, isDesktop } = useScreen()

  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null)

  // Hook pour gérer les notes
  const {
    notes,
    isLoading,
    selectedContactId,
    scopes,
    searchText,
    setSearchText,
    setSelectedContactId,
    setScopes,
  } = useNotes()

  const hasGhost = scopes.includes(Scope.GHOST)
  const hasPrivate = scopes.includes(Scope.PRIVATE)
  const hasProfessional = scopes.includes(Scope.PROFESSIONAL)
  const {
    flatData: contacts,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    fetchNextPage,
  } = usePaginateContacts({
    scope: hasPrivate ? Scope.PRIVATE : hasProfessional ? Scope.PROFESSIONAL : undefined,
    // Ghost is a bonus filter (same behaviour as the main contacts list):
    // selected -> only ghost profiles; unselected -> real contacts (ghosts are
    // excluded only when a Privé/Pro tab is active).
    onlyGhost: hasGhost || undefined,
    noGhost: ((hasPrivate || hasProfessional) && !hasGhost) || undefined,
    search: searchText,
  })

  const handleAddNote = () => {
    if (!isDesktop) {
      navigation.navigate('Menu', {
        screen: 'Notes',
        params: {
          screen: 'SelectNoteRecipient',
        },
      })
    } else {
      setSelectedNoteId('')
    }
  }

  const goToContact = () => {
    navigation.navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId: selectedContactId },
    })
  }
  const handleContactPress = (contact: Contact) => {
    if (selectedContactId === contact.id) {
      setSelectedContactId(undefined)
    } else {
      setSelectedContactId(contact.id)
    }
  }

  const handleNotePress = (noteId: string) => {
    setSelectedNoteId(noteId)
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={isDesktop ? commonStyles.flex315Bordered : commonStyles.flex}>
        <Header
          title={t('notesScreen.title')}
          iconLeft={isWeb ? undefined : 'arrowBack'}
          iconRight={'plus'}
          onPressRight={handleAddNote}
        />
        {!isDesktop && (
          <View style={[styles.searchContainerMobile, !isMobile && commonStyles.row]}>
            <View
              style={[
                styles.searchContainer,
                !isMobile && commonStyles.flex,
                !isMobile && commonStyles.searchContainerMaxWidth,
                !isMobile && styles.noMarginTop,
              ]}
            >
              <SearchBar value={searchText} onChangeText={setSearchText} />
            </View>
            <View style={styles.filtersContainer}>
              <ContactsFilter scope={scopes} onScopeChange={setScopes} />
            </View>
          </View>
        )}
        <ScrollView
          style={styles.notesList}
          contentContainerStyle={styles.fullHeight}
          showsVerticalScrollIndicator={false}
        >
          <View style={isDesktop && styles.notesListContainer}>
            {isDesktop && (
              <View style={commonStyles.flex}>
                <View style={styles.searchContainerMobile}>
                  <View style={styles.searchContainer}>
                    <SearchBar value={searchText} onChangeText={setSearchText} />
                  </View>
                  <View style={styles.filtersContainer}>
                    <ContactsFilter scope={scopes} onScopeChange={setScopes} />
                  </View>
                </View>
                <ContactsList
                  noRightSection
                  contacts={contacts || []}
                  selectedContactId={selectedContactId}
                  onContactPress={handleContactPress}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  isFetching={isFetching}
                  fetchNextPage={fetchNextPage}
                />
              </View>
            )}
            {!isDesktop && (
              <Notes
                profileId={profileId}
                data={notes}
                onNotePress={isDesktop ? handleNotePress : undefined}
              />
            )}
          </View>
        </ScrollView>
      </View>
      {isDesktop && selectedNoteId === null && (
        <View style={commonStyles.flex}>
          {selectedContactId && (
            <SelectedContactDisplay contactId={selectedContactId} onPress={goToContact} />
          )}
          <Notes
            profileId={profileId}
            data={notes}
            onNotePress={isDesktop ? handleNotePress : undefined}
          />
        </View>
      )}
      {isDesktop && selectedNoteId !== null && (
        <EditNote
          noteId={selectedNoteId || undefined}
          onComplete={() => setSelectedNoteId(null)}
          onBack={() => setSelectedNoteId(null)}
          contactId={selectedContactId}
        />
      )}
    </SafeAreaView>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      flexDirection: 'row',
      backgroundColor: '#fff',
    },
    searchContainerMobile: {
      marginBottom: theme.spacing.md,
    },
    searchContainerWeb: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing.lg,
    },
    searchContainer: {
      paddingHorizontal: theme.spacing.lg,
      paddingBottom: theme.spacing.md,
    },
    noMarginTop: {
      paddingBottom: 0,
    },
    filtersContainer: {
      paddingHorizontal: theme.spacing.lg,
    },
    notesListContainer: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    notesList: {
      flex: 1,
    },
    fullHeight: {
      height: '100%',
    },
    loadingContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    errorContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 40,
    },
    errorText: {
      fontSize: theme.fontSize.medium,
      color: theme.colors.error || theme.colors.text,
      textAlign: 'center',
    },
    contactContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      margin: theme.spacing.md,
    },
    contactAvatar: {
      width: 30,
      height: 30,
      borderRadius: 15,
      marginRight: theme.spacing.sm,
    },
    contactName: {
      fontSize: theme.fontSize.medium,
      color: theme.colors.text,
    },
  })
